// Beta-0.5D-A · 社内用語 — union view + conflict review queue.
// READ-ONLY in this pass. Physical consolidation is Stage-B.

import { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowsClockwise, Warning, Info, CircleNotch, MagnifyingGlass, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const SOURCE_BADGE = {
  company_dictionary:  { label: '会社辞書', cls: 'bg-zinc-100 text-zinc-700' },
  ai_glossary:         { label: 'AI用語集', cls: 'bg-violet-100 text-violet-800' },
  sensbey_terminology: { label: '社内用語辞書', cls: 'bg-teal-100 text-teal-800' },
};

export default function TerminologyPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/api/terminology/union`, { headers: authHeader() });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || '用語データの取得に失敗しました');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rows = (data?.rows || []).filter((r) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (r.term_ja || '').toLowerCase().includes(s)
        || (r.term_en || '').toLowerCase().includes(s)
        || (r.aliases || []).some((a) => (a || '').toLowerCase().includes(s));
  });

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 space-y-5" data-testid="terminology-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">社内用語</h1>
          <p className="text-[12px] text-zinc-600 mt-1">
            会社辞書・AI用語集・社内用語辞書の統合ビュー。この画面は表示のみ。
            編集はそれぞれの元ソースで行います（今後のリリースで統合予定）。
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} className="h-9 text-[12px]"
          data-testid="terminology-refresh">
          {loading ? <CircleNotch size={13} className="animate-spin mr-1" />
                    : <ArrowsClockwise size={13} className="mr-1" />} 更新
        </Button>
      </div>

      {err && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-800"
          data-testid="terminology-error">
          <Warning size={13} className="inline mr-1" />
          {typeof err === 'string' ? err : (err.message || 'エラー')}
        </div>
      )}

      {data && (
        <>
          {/* Source counts */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {Object.entries(data.counts_by_source).map(([src, n]) => (
              <div key={src} className="rounded-lg border border-zinc-200 bg-white px-4 py-3"
                data-testid={`terminology-source-${src}`}>
                <p className="text-[11px] text-zinc-500 uppercase tracking-wider">
                  {SOURCE_BADGE[src]?.label || src}
                </p>
                <p className="mt-1 text-[24px] tabular-nums font-semibold text-teal-700">
                  {n.toLocaleString('ja-JP')}
                </p>
                <p className="text-[10.5px] text-zinc-400 mt-0.5 font-mono">{src}</p>
              </div>
            ))}
          </div>

          {/* Conflict queue */}
          {data.conflict_count > 0 ? (
            <div className="rounded-lg border-2 border-amber-300 bg-amber-50/60 overflow-hidden"
              data-testid="terminology-conflicts">
              <header className="px-4 py-3 border-b border-amber-300 bg-amber-100/60">
                <p className="flex items-center gap-1.5 text-[12px] font-bold text-amber-900">
                  <Warning size={13} weight="fill" /> 用語の衝突 {data.conflict_count} 件
                </p>
                <p className="mt-1 text-[11.5px] text-amber-900/80">
                  同じ用語が複数のソースで異なる正規表記になっています。
                  統合前にどちらを採用するか決定してください。
                </p>
              </header>
              <div className="divide-y divide-amber-200">
                {data.conflicts.map((c) => (
                  <div key={c.key} className="px-4 py-3" data-testid={`terminology-conflict-${c.key}`}>
                    <p className="text-[13px] font-semibold text-zinc-900">
                      {c.key} <span className="text-[10.5px] font-normal text-amber-800 ml-2">
                        {c.sources.map((s) => SOURCE_BADGE[s]?.label || s).join(' / ')}
                      </span>
                    </p>
                    <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[11.5px]">
                      <div>
                        <span className="text-zinc-500">日本語: </span>
                        {(c.term_ja_variants || []).map((v, i) => (
                          <span key={i} className="inline-block mr-2 px-1.5 py-0.5 rounded bg-white border border-amber-300 font-mono text-[11px]">
                            {v || '（空）'}
                          </span>
                        ))}
                      </div>
                      <div>
                        <span className="text-zinc-500">英語: </span>
                        {((c.term_en_variants || []).length > 0
                          ? c.term_en_variants
                          : ['（空）']).map((v, i) => (
                          <span key={i} className="inline-block mr-2 px-1.5 py-0.5 rounded bg-white border border-amber-300 font-mono text-[11px]">
                            {v}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-[12px] text-emerald-900 flex items-center gap-2"
              data-testid="terminology-no-conflicts">
              <Info size={13} weight="fill" /> 衝突は検出されていません。すべてのソースで正規表記が一致しています。
            </div>
          )}

          {/* Search + rows */}
          <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
            <div className="px-3 py-2 border-b border-zinc-200 flex items-center gap-2">
              <div className="relative flex-1">
                <MagnifyingGlass size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="用語を検索（日本語・英語・エイリアス）"
                  data-testid="terminology-filter-input"
                  className="w-full h-8 pl-8 pr-8 rounded-md border border-zinc-200 text-[12px] placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-teal-200 focus:border-teal-400" />
                {q && (
                  <button type="button" onClick={() => setQ('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                    data-testid="terminology-filter-clear">
                    <X size={11} weight="bold" />
                  </button>
                )}
              </div>
              <span className="text-[10.5px] text-zinc-400 tabular-nums">
                {rows.length} / {data.total_terms}
              </span>
            </div>
            <div className="max-h-[520px] overflow-y-auto">
              <table className="w-full text-[12px]">
                <thead className="bg-zinc-50 text-zinc-500 sticky top-0">
                  <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:text-left text-[10.5px] uppercase tracking-wider">
                    <th className="w-1/3">日本語</th>
                    <th className="w-1/4">英語</th>
                    <th>エイリアス / 備考</th>
                    <th className="w-24">ソース</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {rows.map((r, i) => (
                    <tr key={`${r.source}#${r.key}#${i}`} className="hover:bg-zinc-50/50"
                      data-testid={`terminology-row-${i}`}>
                      <td className="px-3 py-2 text-zinc-900 font-medium">{r.term_ja || '—'}</td>
                      <td className="px-3 py-2 text-zinc-700 font-mono text-[11.5px]">{r.term_en || '—'}</td>
                      <td className="px-3 py-2 text-zinc-600">
                        {(r.aliases || []).slice(0, 5).map((a, j) => (
                          <span key={j} className="inline-block mr-1 mb-0.5 px-1.5 py-0.5 rounded bg-zinc-100 text-[10.5px] font-mono">
                            {a}
                          </span>
                        ))}
                        {r.notes && <p className="text-[10.5px] text-zinc-400 mt-0.5">{r.notes}</p>}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[9.5px] font-semibold ${SOURCE_BADGE[r.source]?.cls || 'bg-zinc-100'}`}>
                          {SOURCE_BADGE[r.source]?.label || r.source}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-3 py-6 text-center text-[11.5px] text-zinc-400"
                        data-testid="terminology-empty">
                        該当する用語がありません
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 text-[11.5px] text-zinc-600"
            data-testid="terminology-consumers">
            <p className="flex items-center gap-1"><Info size={12} />
              <span>この統合ビューを使う機能:</span></p>
            <ul className="mt-1 list-disc list-inside space-y-0.5">
              {(data.readers_ja || []).map((s) => <li key={s}>{s}</li>)}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
